import type {
  ErrorEvent,
  ExclusiveEventHintOrCaptureContext,
} from '@sentry/browser';
import { BffRequestError } from './bffRequestError';

const DYNAMIC_ROUTES = [
  { prefix: '/ladda-ner/', pattern: '/ladda-ner/:token' },
  { prefix: '/bekrafta_konto/', pattern: '/bekrafta_konto/:code' },
  { prefix: '/switch-user/', pattern: '/switch-user/:customerAccountId' },
];

export function normalizePagePath(pathname: string): string {
  const route = DYNAMIC_ROUTES.find(({ prefix }) =>
    pathname.startsWith(prefix),
  );
  return route ? route.pattern : pathname;
}

// Query strings can carry OIDC codes, so only origin and normalized path are kept.
function scrubUrl(url: string): string {
  try {
    const { origin, pathname } = new URL(url);
    return `${origin}${normalizePagePath(pathname)}`;
  } catch {
    const [path] = url.split(/[?#]/);
    return normalizePagePath(path);
  }
}

function getTraceId(details: unknown): string | undefined {
  if (details && typeof details === 'object' && 'traceId' in details) {
    const { traceId } = details as { traceId: unknown };
    return typeof traceId === 'string' ? traceId : undefined;
  }
  return undefined;
}

export function buildQueryErrorContext(
  error: unknown,
  queryName: string | undefined,
): ExclusiveEventHintOrCaptureContext {
  const contexts = { query: { name: queryName ?? 'unknown' } };

  if (!(error instanceof BffRequestError)) {
    return { contexts };
  }

  const status = String(error.status ?? 'network');
  const tags: Record<string, string> = {
    'api.endpoint': error.endpoint,
    'api.method': error.method,
    'api.status': status,
  };
  const traceId = getTraceId(error.details);
  if (traceId) {
    tags['api.traceId'] = traceId;
  }
  for (const [name, value] of Object.entries(error.pathParams)) {
    tags[`api.param.${name}`] = value;
  }

  return {
    fingerprint: ['bff', error.method, error.endpoint, status],
    tags,
    contexts: {
      ...contexts,
      bff: {
        details: {
          title:
            error.details &&
            typeof error.details === 'object' &&
            'title' in error.details &&
            typeof error.details.title === 'string'
              ? error.details.title
              : undefined,
          traceId,
        },
      },
    },
  };
}

export function sanitizeEvent(event: ErrorEvent, pathname: string): ErrorEvent {
  delete event.user;
  event.tags = { ...event.tags, page: normalizePagePath(pathname) };
  if (event.request?.url) {
    event.request.url = scrubUrl(event.request.url);
  }
  if (event.request) {
    delete event.request.query_string;
  }
  return event;
}
